import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";
import SquareAILogo from "@/components/chat/SquareAILogo";
import { useChat } from "@/context/ChatContext";

/**
 * Heavy chat UI is only fetched from the JS bundle when the user actually
 * opens the chat. On first paint we only ship the small floating button.
 */
const SquareAIChatPanel = lazy(() =>
  import(/* webpackChunkName: "square-ai-chat" */ "@/components/chat/SquareAIChatPanel")
);

/**
 * Floating right-side widget:
 *  - Vertical CALL NOW link
 *  - Square AI chat button (opens chat panel)
 *  - Email quick link
 */
const SquareAIWidget = () => {
  const { isOpen, open } = useChat();

  return (
    <>
      <div
        className="fixed z-30 hidden md:flex flex-col items-center gap-6"
        data-testid="four-square-bot"
        style={{ right: 14, top: "50%", transform: "translateY(-50%)" }}
      >
        {/* Vertical Call Now block */}
        <a
          href="tel:+918681833444"
          className="flex flex-col items-center justify-center"
          aria-label="Call Now"
          data-testid="bot-call-now"
        >
          <span
            className="w-10 h-10 rounded-full inline-flex items-center justify-center mb-3"
            style={{
              border: "1px solid rgba(77,215,238,0.4)",
              background: "rgba(77,215,238,0.06)",
              color: "#4dd7ee",
            }}
          >
            <Phone size={16} />
          </span>
          <span
            className="text-[10px] tracking-[0.28em] text-[color:var(--text-dim)]"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            CALL NOW
          </span>
        </a>

        {/* Square AI chat launcher */}
        <motion.button
          type="button"
          onClick={open}
          className="relative rounded-full"
          aria-label="Open Square Bot chat"
          data-testid="square-ai-launcher"
          animate={
            !isOpen
              ? { boxShadow: [
                  "0 0 0 0 rgba(77,215,238,0.55)",
                  "0 0 0 12px rgba(77,215,238,0.0)",
                  "0 0 0 0 rgba(77,215,238,0.0)",
                ] }
              : { boxShadow: "0 0 0 0 rgba(77,215,238,0)" }
          }
          transition={{ duration: 2.4, repeat: isOpen ? 0 : Infinity }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
          style={{ background: "transparent", border: "none", padding: 0 }}
        >
          <SquareAILogo size={54} />
          <span
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
            style={{ background: "#45e0a6", boxShadow: "0 0 8px rgba(69,224,166,0.9)" }}
            aria-hidden
          />
        </motion.button>

        {/* Email icon */}
        <a
          href="mailto:foursquaretechsol@gmail.com"
          className="w-10 h-10 rounded-full inline-flex items-center justify-center"
          style={{
            border: "1px solid rgba(77,215,238,0.4)",
            background: "rgba(77,215,238,0.06)",
            color: "#4dd7ee",
          }}
          aria-label="Email Four Square"
          data-testid="bot-email"
        >
          <Mail size={16} />
        </a>
      </div>

      {/* Mobile floating button */}
      <motion.button
        type="button"
        onClick={open}
        className="fixed z-30 md:hidden rounded-full"
        aria-label="Open Square Bot chat"
        data-testid="square-ai-launcher-mobile"
        style={{
          right: 14,
          bottom: 76,
          background: "transparent",
          border: "none",
          padding: 0,
        }}
        animate={
          !isOpen
            ? { boxShadow: [
                "0 0 0 0 rgba(77,215,238,0.55)",
                "0 0 0 12px rgba(77,215,238,0.0)",
                "0 0 0 0 rgba(77,215,238,0.0)",
              ] }
            : { boxShadow: "0 0 0 0 rgba(77,215,238,0)" }
        }
        transition={{ duration: 2.4, repeat: isOpen ? 0 : Infinity }}
        whileTap={{ scale: 0.94 }}
      >
        <SquareAILogo size={52} />
      </motion.button>

      {/* Lazy-loaded chat panel */}
      {isOpen && (
        <Suspense fallback={null}>
          <SquareAIChatPanel />
        </Suspense>
      )}
    </>
  );
};

export default SquareAIWidget;
